export type InspectionOutcomeBadgeVariant =
    'pass' | 'fail' | 'warning' | 'manual_review' | 'neutral';

export type TransactionStatusBadgeVariant =
    'created' | 'waiting' | 'timed_out' | 'ready' | 'inspected' | 'actions' | 'completed' | 'neutral';

export const VISION_MODES = ['disabled', 'monitor_only', 'enforced'] as const;

export const INSPECTION_OUTCOMES = ['PASS', 'FAIL', 'WARNING', 'MANUAL_REVIEW'] as const;

export const VEHICLE_TRANSACTION_STATUSES = [
    'CREATED',
    'WAITING_FOR_REQUIRED_EVENTS',
    'TIMED_OUT',
    'READY_FOR_INSPECTION_POLICY',
    'INSPECTION_COMPLETED',
    'ACTIONS_EXECUTED',
    'COMPLETED',
] as const;

export const PROVIDER_EVENT_PROCESSING_STATUSES = [
    'RECEIVED',
    'PENDING_CORRELATION',
    'MATCHED',
    'TIMED_OUT',
    'COMPLETED',
] as const;

export type ProviderEventStatusBadgeVariant =
    'received' | 'pending' | 'matched' | 'timed_out' | 'completed' | 'neutral';

export const ACCESS_DECISIONS = ['ALLOW', 'DENY'] as const;

export const TRANSACTION_STATUS_GROUP_OPTIONS = [
    {value: 'processing', label: 'Processing'},
    {value: 'waiting', label: 'Waiting'},
    {value: 'completed', label: 'Completed'},
    {value: 'needs_attention', label: 'Needs Attention'},
    {value: 'failed', label: 'Failed'},
] as const;

export const DEFAULT_VISION_POLICY = {
    vision_mode: 'disabled' as const,
    vision_capabilities: [] as string[],
    vision_provider: '',
    enforce_threshold: 0.85,
    vision_failure_policy: 'fail_open' as const,
    vision_timeout_seconds: 3,
    access_rules: {},
    analytics_summary: [] as Array<{analytics_key: string; name: string; provider: string}>,
};

export function resolveInspectionOutcomeBadge(
    outcome: string | null | undefined,
): { label: string; variant: InspectionOutcomeBadgeVariant } | null {
    const normalized = (outcome || '').trim().toUpperCase();
    if (!normalized) {
        return null;
    }
    switch (normalized) {
        case 'PASS':
            return {label: 'Pass', variant: 'pass'};
        case 'FAIL':
            return {label: 'Fail', variant: 'fail'};
        case 'WARNING':
            return {label: 'Warning', variant: 'warning'};
        case 'MANUAL_REVIEW':
            return {label: 'Manual Review', variant: 'manual_review'};
        default:
            return {label: normalized.replace(/_/g, ' '), variant: 'neutral'};
    }
}

export function transactionNeedsOperationalAttention(
    transaction: {
        status?: string | null;
        timed_out_at?: string | null;
        pipeline?: { vehicle_log_metadata?: Record<string, unknown> };
    },
): boolean {
    const status = (transaction.status || '').trim().toUpperCase();
    if (status === 'TIMED_OUT') {
        return true;
    }
    if (transaction.timed_out_at) {
        return true;
    }
    if (status === 'READY_FOR_INSPECTION_POLICY') {
        return true;
    }
    const outcome = transaction.pipeline?.vehicle_log_metadata?.['vte_inspection_outcome'];
    if (typeof outcome === 'string') {
        const normalized = outcome.trim().toUpperCase();
        if (normalized === 'MANUAL_REVIEW' || normalized === 'WARNING') {
            return true;
        }
    }
    return false;
}

export function resolveOperationalAttentionBadge(
    transaction: {
        status?: string | null;
        timed_out_at?: string | null;
        pipeline?: { vehicle_log_metadata?: Record<string, unknown> };
    },
): { label: string; hint: string } | null {
    if (!transactionNeedsOperationalAttention(transaction)) {
        return null;
    }

    const status = (transaction.status || '').trim().toUpperCase();
    const outcome = transaction.pipeline?.vehicle_log_metadata?.['vte_inspection_outcome'];
    const normalizedOutcome = typeof outcome === 'string' ? outcome.trim().toUpperCase() : '';

    if (normalizedOutcome === 'MANUAL_REVIEW') {
        return {
            label: 'Needs Attention',
            hint: 'Manual review required — transaction status reflects the current lifecycle stage.',
        };
    }
    if (normalizedOutcome === 'WARNING') {
        return {
            label: 'Needs Attention',
            hint: 'Inspection warning — transaction status reflects the current lifecycle stage.',
        };
    }
    if (status === 'TIMED_OUT' || transaction.timed_out_at) {
        return {
            label: 'Needs Attention',
            hint: 'Correlation or provider timeout — transaction status reflects the current lifecycle stage.',
        };
    }
    if (status === 'READY_FOR_INSPECTION_POLICY') {
        return {
            label: 'Needs Attention',
            hint: 'Awaiting inspection policy — transaction status reflects the current lifecycle stage.',
        };
    }

    return {
        label: 'Needs Attention',
        hint: 'Operational attention required — transaction status reflects the current lifecycle stage.',
    };
}

export function resolveTransactionStatusBadge(
    status: string | null | undefined,
): { label: string; variant: TransactionStatusBadgeVariant } | null {
    const normalized = (status || '').trim().toUpperCase();
    if (!normalized) {
        return null;
    }
    switch (normalized) {
        case 'CREATED':
            return {label: 'Created', variant: 'created'};
        case 'WAITING_FOR_REQUIRED_EVENTS':
            return {label: 'Waiting', variant: 'waiting'};
        case 'TIMED_OUT':
            return {label: 'Timed Out', variant: 'timed_out'};
        case 'READY_FOR_INSPECTION_POLICY':
            return {label: 'Ready', variant: 'ready'};
        case 'INSPECTION_COMPLETED':
            return {label: 'Inspected', variant: 'inspected'};
        case 'ACTIONS_EXECUTED':
            return {label: 'Actions Done', variant: 'actions'};
        case 'COMPLETED':
            return {label: 'Completed', variant: 'completed'};
        default:
            return {label: normalized.replace(/_/g, ' '), variant: 'neutral'};
    }
}

export function resolveProviderEventStatusBadge(
    status: string | null | undefined,
): { label: string; variant: ProviderEventStatusBadgeVariant } | null {
    const normalized = (status || '').trim().toUpperCase();
    if (!normalized) {
        return null;
    }
    switch (normalized) {
        case 'RECEIVED':
            return {label: 'Received', variant: 'received'};
        case 'PENDING_CORRELATION':
            return {label: 'Pending', variant: 'pending'};
        case 'MATCHED':
            return {label: 'Matched', variant: 'matched'};
        case 'TIMED_OUT':
            return {label: 'Timed Out', variant: 'timed_out'};
        case 'COMPLETED':
            return {label: 'Completed', variant: 'completed'};
        default:
            return {label: normalized.replace(/_/g, ' '), variant: 'neutral'};
    }
}

export function resolveAccessDecisionBadge(
    decision: string | null | undefined,
): { label: string; variant: 'allow' | 'deny' | 'neutral' } | null {
    const normalized = (decision || '').trim().toUpperCase();
    if (!normalized) {
        return null;
    }
    if (normalized === 'ALLOW') {
        return {label: 'Allow', variant: 'allow'};
    }
    if (normalized === 'DENY') {
        return {label: 'Deny', variant: 'deny'};
    }
    return {label: normalized, variant: 'neutral'};
}
